import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, FolderOpen, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultDocuments = [
  {
    id: '1',
    title: 'Framework Memorandum of Understanding (MoU)',
    description: 'The official framework agreement establishing the foundation for UIEHEC partnerships.',
    category: 'Legal',
    status: 'available',
    file_url: '/mou.pdf'
  },
  {
    id: '2',
    title: 'UIEHEC Statute',
    description: 'The governing document outlining the structure and regulations of UIEHEC.',
    category: 'Legal',
    status: 'coming_soon',
    file_url: '#'
  },
  {
    id: '3',
    title: 'Annual Reports',
    description: 'Comprehensive yearly reports on UIEHEC activities, achievements, and impact.',
    category: 'Reports',
    status: 'in_preparation',
    file_url: '#'
  },
  {
    id: '4',
    title: 'Financial and Transparency Reports',
    description: 'Detailed financial statements and transparency documentation.',
    category: 'Reports',
    status: 'coming_soon',
    file_url: '#'
  },
];

export default function DocumentsPage() {
  const [documents, setDocuments] = useState(defaultDocuments);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`${API}/documents`);
        if (response.data.length > 0) {
          setDocuments(response.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    fetchDocuments();
  }, []);

  const categories = ['All', ...new Set(documents.map(d => d.category))];
  
  const filteredDocuments = selectedCategory === 'All' 
    ? documents 
    : documents.filter(d => d.category === selectedCategory);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Available</span>;
      case 'coming_soon':
        return <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">Coming Soon</span>;
      case 'in_preparation':
        return <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">In Preparation</span>;
      default:
        return null;
    }
  };

  const getIcon = (category) => {
    switch (category) {
      case 'Legal':
        return '📘';
      case 'Reports':
        return '📙';
      default:
        return '📄';
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <FolderOpen className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Documents
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Official Documents
            </h1>
            <p className="text-slate-300">
              Access important documents, guidelines, and resources 
              related to UIEHEC and membership.
            </p>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category 
                  ? 'bg-[#0E285B] text-white' 
                  : 'border-slate-300 text-slate-600'
                }
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Documents Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {filteredDocuments.map((doc, index) => (
              <div 
                key={doc.id}
                className="bg-white p-6 rounded-sm border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`document-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl">{getIcon(doc.category)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B]">
                        {doc.title}
                      </h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    <p className="text-slate-600 text-sm mb-4">
                      {doc.description}
                    </p>
                    {doc.status === 'available' && doc.file_url !== '#' ? (
                      <div className="flex gap-2">
                        <a 
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="gap-2 border-[#0E285B] text-[#0E285B] hover:bg-[#0E285B]/10"
                          >
                            <ExternalLink className="h-4 w-4" />
                            View
                          </Button>
                        </a>
                        <a 
                          href={doc.file_url}
                          download
                        >
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="gap-2 border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </a>
                      </div>
                    ) : doc.status === 'available' ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="gap-2 border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                        disabled
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </Button>
                    ) : (
                      <p className="text-sm text-slate-400 italic">
                        {doc.status === 'coming_soon' ? 'Coming Soon' : 'In Preparation'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No documents available in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
